import { useEffect, useState } from 'react';
import axios from 'axios';
import { API } from '@/App';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Plus, Edit2, Trash2, MapPin, Eye } from 'lucide-react';
import { toast } from 'sonner';
import { DeleteConfirmDialog } from '@/components/DeleteConfirmDialog';
import { CountryCombobox } from '@/components/CountryCombobox';
import { CountryFilter } from '@/components/CountryFilter';
import { DEFAULT_COUNTRY } from '@/lib/countries';

const emptyForm = { venue_name: '', site_name: '', street: '', suburb: '', city: '', postcode: '', country: '' };

// Format address for display
const formatAddress = (venue) => {
  const lines = [];
  if (venue.street) lines.push(venue.street);
  if (venue.suburb) lines.push(venue.suburb);
  lines.push(`${venue.city || ''} ${venue.postcode?.padStart(4, '0') || ''}`.trim());
  return lines.filter(Boolean);
};

// Generate Google Maps URL
const getGoogleMapsUrl = (venue) => {
  const parts = [venue.street, venue.suburb, venue.city, venue.postcode].filter(Boolean);
  return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(parts.join(', '))}`;
};

export default function VenuesPage({ embedded = false, country: externalCountry, autoOpenCreate = false, onAutoOpenConsumed }) {
  const [currentUser, setCurrentUser] = useState(null);
  const [venues, setVenues] = useState([]);
  const [open, setOpen] = useState(false);
  const [editingVenue, setEditingVenue] = useState(null);
  const [viewOpen, setViewOpen] = useState(false);
  const [viewingVenue, setViewingVenue] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [formData, setFormData] = useState(emptyForm);
  const [internalCountry, setInternalCountry] = useState('');
  // Parent (People & Places) owns the country filter when embedded.
  const countryFilter = embedded ? (externalCountry || '') : internalCountry;
  const setCountryFilter = setInternalCountry;

  const isSuper = currentUser?.role === 'super';

  const canEdit = (venue) => {
    if (!currentUser) return false;
    if (isSuper) return true;
    return !!currentUser.club_id && currentUser.club_id === venue.created_by_club_id;
  };

  const fetchVenues = async (country = countryFilter) => {
    try {
      const params = country ? { country } : {};
      const response = await axios.get(`${API}/venues`, { withCredentials: true, params });
      setVenues(response.data);
    } catch (error) {
      console.error('Error fetching venues:', error);
    }
  };

  useEffect(() => {
    axios.get(`${API}/auth/me`, { withCredentials: true })
      .then(r => setCurrentUser(r.data))
      .catch(() => {});
  }, []);

  // Refetch whenever the country filter changes (including its
  // initial auto-resolve from ``CountryFilter``).
  useEffect(() => {
    if (!countryFilter) return; // wait until CountryFilter reports its default
    fetchVenues(countryFilter);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [countryFilter]);

  const resetForm = () => {
    setFormData(emptyForm);
    setEditingVenue(null);
  };

  /** Called when clicking "Add Venue". Fetch the caller's default country
   * so the form pre-fills, then open the dialog. Falls back to the
   * global default if the endpoint fails for any reason. */
  const openCreateDialog = async () => {
    let country = DEFAULT_COUNTRY;
    try {
      const r = await axios.get(`${API}/countries/my-default`, { withCredentials: true });
      if (r.data?.country) country = r.data.country;
    } catch { /* keep the fallback */ }
    setFormData({ ...emptyForm, country });
    setEditingVenue(null);
    setOpen(true);
  };

  // Support ?action=create deep-link from the Dashboard Quick Action.
  useEffect(() => {
    if (!autoOpenCreate) return;
    openCreateDialog();
    if (onAutoOpenConsumed) onAutoOpenConsumed();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoOpenCreate]);

  const handleEdit = (venue) => {
    setFormData({
      venue_name: venue.venue_name,
      site_name: venue.site_name || '',
      street: venue.street || '',
      suburb: venue.suburb || '',
      city: venue.city || '',
      postcode: venue.postcode || '',
      country: venue.country || DEFAULT_COUNTRY,
    });
    setEditingVenue(venue);
    setOpen(true);
  };

  const handleView = (venue) => {
    setViewingVenue(venue);
    setViewOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const postcode = formData.postcode.padStart(4, '0');
    const submitData = { ...formData, postcode };

    try {
      if (editingVenue) {
        await axios.patch(`${API}/venues/${editingVenue.venue_id}`, submitData, { withCredentials: true });
        toast.success('Venue updated!');
      } else {
        await axios.post(`${API}/venues`, submitData, { withCredentials: true });
        toast.success('Venue created!');
      }
      setOpen(false);
      resetForm();
      fetchVenues(countryFilter);
    } catch (error) {
      toast.error(error.response?.data?.detail || (editingVenue ? 'Failed to update venue' : 'Failed to create venue'));
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await axios.delete(`${API}/venues/${deleteTarget.venue_id}`, { withCredentials: true });
      toast.success('Venue deleted');
      setDeleteDialogOpen(false);
      setDeleteTarget(null);
      fetchVenues(countryFilter);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete venue');
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className={embedded ? 'space-y-4' : 'space-y-6 fade-in'}>
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-4">
          {embedded ? (
            <div>
              <h2 className="text-xl font-semibold" style={{ color: '#2D4A3E' }}>Venues</h2>
              <p className="text-sm text-muted-foreground">Manage contact and location information for the places we skate</p>
            </div>
          ) : (
            <h1 className="text-4xl font-bold" style={{ fontFamily: 'Manrope, sans-serif', color: '#2D4A3E' }}>
              Venue Management
            </h1>
          )}
          {!embedded && <CountryFilter value={countryFilter} onChange={setCountryFilter} testId="venues-country-filter" />}
        </div>
        <Dialog open={open} onOpenChange={(val) => { setOpen(val); if (!val) resetForm(); }}>
          <Button className="btn-primary" data-testid="create-venue-button" onClick={openCreateDialog}>
            <Plus className="mr-2 h-5 w-5" />
            New Venue
          </Button>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{editingVenue ? 'Edit Venue' : 'Create Venue'}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label>Venue Name *</Label>
                <Input value={formData.venue_name} onChange={(e) => setFormData({ ...formData, venue_name: e.target.value })} required data-testid="venue-name-input" />
              </div>
              <div>
                <Label>Site Name (Optional)</Label>
                <Input value={formData.site_name} onChange={(e) => setFormData({ ...formData, site_name: e.target.value })} data-testid="venue-sitename-input" />
              </div>
              <div>
                <Label>Street *</Label>
                <Input value={formData.street} onChange={(e) => setFormData({ ...formData, street: e.target.value })} required data-testid="venue-street-input" />
              </div>
              <div>
                <Label>Suburb (Optional)</Label>
                <Input value={formData.suburb} onChange={(e) => setFormData({ ...formData, suburb: e.target.value })} data-testid="venue-suburb-input" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>City *</Label>
                  <Input value={formData.city} onChange={(e) => setFormData({ ...formData, city: e.target.value })} required data-testid="venue-city-input" />
                </div>
                <div>
                  <Label>Postcode * (4 digits)</Label>
                  <Input
                    value={formData.postcode}
                    onChange={(e) => setFormData({ ...formData, postcode: e.target.value.replace(/\D/g, '').slice(0, 4) })}
                    required
                    pattern="\d{4}"
                    maxLength={4}
                    data-testid="venue-postcode-input"
                  />
                </div>
              </div>
              <div>
                <Label>Country *</Label>
                <div className="mt-1">
                  <CountryCombobox
                    value={formData.country}
                    onChange={(v) => setFormData({ ...formData, country: v })}
                    testId="venue-country"
                  />
                </div>
              </div>
              <Button type="submit" className="w-full btn-primary" data-testid="venue-submit-button">
                {editingVenue ? 'Update Venue' : 'Create Venue'}
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <Card className="p-6">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-stone-200">
                <th className="text-left py-3 px-2 font-semibold w-[450px]">Venue Name</th>
                <th className="text-left py-3 px-2 font-semibold w-[350px]">Site Name</th>
                <th className="text-left py-3 px-2 font-semibold">Address</th>
                <th className="text-center py-3 px-2 font-semibold w-[150px]">Map</th>
                <th className="text-center py-3 px-2 font-semibold w-[180px]">Actions</th>
              </tr>
            </thead>
            <tbody>
              {[...venues].sort((a, b) => a.venue_name.localeCompare(b.venue_name)).map(venue => {
                const editable = canEdit(venue);
                return (
                  <tr key={venue.venue_id} className="border-b border-stone-100 hover:bg-stone-50" data-testid={`venue-row-${venue.venue_id}`}>
                    <td className="py-3 px-2 font-medium w-[450px]">{venue.venue_name}</td>
                    <td className="py-3 px-2 text-sm text-muted-foreground w-[350px]">{venue.site_name || ''}</td>
                    <td className="py-3 px-2 text-sm">
                      {formatAddress(venue).map((line, idx) => (
                        <div key={idx}>{line}</div>
                      ))}
                    </td>
                    <td className="py-3 px-2 text-center">
                      <a
                        href={getGoogleMapsUrl(venue)}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1 text-sm text-blue-600 hover:text-blue-800 hover:underline"
                        data-testid={`map-link-${venue.venue_id}`}
                      >
                        <MapPin className="h-4 w-4" />
                        View Map
                      </a>
                    </td>
                    <td className="py-3 px-2">
                      <div className="flex items-center justify-center gap-2">
                        <button onClick={() => handleView(venue)} className="action-icon" title="View" data-testid={`view-venue-${venue.venue_id}`}>
                          <Eye className="h-4 w-4" />
                        </button>
                        {editable && (
                          <button onClick={() => handleEdit(venue)} className="action-icon" title="Edit" data-testid={`edit-venue-${venue.venue_id}`}>
                            <Edit2 className="h-4 w-4" />
                          </button>
                        )}
                        {isSuper && (
                          <button
                            onClick={() => { setDeleteTarget(venue); setDeleteDialogOpen(true); }}
                            className="action-icon text-red-600 hover:text-red-800"
                            title="Delete"
                            data-testid={`delete-venue-${venue.venue_id}`}
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          {venues.length === 0 && (
            <div className="text-center py-12 text-muted-foreground">
              No venues yet. Create your first one!
            </div>
          )}
        </div>
      </Card>

      {/* View (read-only) Dialog */}
      <Dialog open={viewOpen} onOpenChange={setViewOpen}>
        <DialogContent data-testid="view-venue-dialog">
          <DialogHeader>
            <DialogTitle>{viewingVenue?.venue_name}</DialogTitle>
          </DialogHeader>
          {viewingVenue && (
            <div className="space-y-3 text-sm">
              <div>
                <div className="text-xs text-muted-foreground">Venue Name</div>
                <div>{viewingVenue.venue_name}</div>
              </div>
              <div>
                <div className="text-xs text-muted-foreground">Site Name</div>
                <div>{viewingVenue.site_name || '-'}</div>
              </div>
              <div>
                <div className="text-xs text-muted-foreground">Street</div>
                <div>{viewingVenue.street || '-'}</div>
              </div>
              <div>
                <div className="text-xs text-muted-foreground">Suburb</div>
                <div>{viewingVenue.suburb || '-'}</div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <div className="text-xs text-muted-foreground">City</div>
                  <div>{viewingVenue.city || '-'}</div>
                </div>
                <div>
                  <div className="text-xs text-muted-foreground">Postcode</div>
                  <div>{viewingVenue.postcode || '-'}</div>
                </div>
              </div>
              <div>
                <div className="text-xs text-muted-foreground">Country</div>
                <div>{viewingVenue.country || '-'}</div>
              </div>
              <div className="pt-3">
                <a
                  href={getGoogleMapsUrl(viewingVenue)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-blue-600 text-white text-sm hover:bg-blue-700"
                  data-testid="view-venue-map-btn"
                >
                  <MapPin className="h-4 w-4" />
                  Open in Google Maps
                </a>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <DeleteConfirmDialog
        open={deleteDialogOpen}
        onOpenChange={setDeleteDialogOpen}
        itemName={deleteTarget?.venue_name || ''}
        onConfirm={confirmDelete}
        loading={deleting}
      />
    </div>
  );
}
